<template>
    <div class="flex flex-col space-y-6">
        <div class="bg-white shadow sm:rounded-lg">
            <div class="px-4 py-8 sm:p-6">
                <div
                    class="md:flex md:items-center md:justify-between md:space-x-5"
                >
                    <div class="flex items-start space-x-5">
                        <div class="flex-shrink-0">
                            <div v-if="courseData" class="relative">
                                <img
                                    class="w-16 h-16 p-1 rounded-full ring-2 ring-gray-300 dark:ring-gray-500"
                                    :src="logoImage"
                                    alt=""
                                />
                                <span
                                    class="-bottom-1 left-10 absolute w-4 h-4 bg-lime-600 border-2 border-white dark:border-gray-800 rounded-full"
                                ></span>
                            </div>
                            <div v-else role="status" class="animate-pulse">
                                <div class="p-0">
                                    <svg
                                        class="w-16 h-16 text-gray-200 dark:text-gray-700 me-4"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <!--
        Use vertical padding to simulate center alignment when both lines of text are one line,
        but preserve the same layout if the text wraps without making the image jump around.
      -->
                        <div v-if="courseData" class="pt-1.5">
                            <h1
                                class="text-2xl font-bold text-gray-900 tracking-tight pb-1"
                            >
                                {{ courseData.courseName }}
                            </h1>
                            <p class="text-md font-medium text-gray-500">
                                {{
                                    formatPhoneNumber(
                                        courseData.vapiGolfCoursePhone,
                                    )
                                }}
                            </p>
                        </div>
                        <div
                            v-else
                            role="status"
                            class="pt-1.5 max-w-sm animate-pulse"
                        >
                            <div
                                class="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-56 mb-3"
                            ></div>
                            <div
                                class="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"
                            ></div>
                            <span class="sr-only">Loading...</span>
                        </div>
                    </div>
                    <StartChat></StartChat>
                </div>
            </div>
        </div>
        <div
            v-if="recentChats && recentChats.length > 0"
            class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow"
        >
            <div class="px-4 py-5 sm:p-6">
                <h1 class="text-lg font-medium text-gray-600 tracking-tight">
                    Recent Chats
                </h1>
            </div>
            <div class="px-4 py-5 sm:p-6">
                <RecentChats :recent-chats="recentChats"></RecentChats>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
definePageMeta({
    requiresAuth: true,
    middleware: ["auth", "course"],
});
import * as Sentry from "@sentry/vue";
import RecentChats from "@/components/RecentChats.vue";
import StartChat from "@/components/StartChat.vue";
import { useApiFetch } from "@/composables/fetch";
import { useFirebase } from "@/composables/firebase";
import { useCourse } from "@/composables/course";
const { courseData, logoImage }: any = useCourse();
const { customer, user } = useFirebase();
const { useDefaultFetch } = useApiFetch();

const {
    execute: fetchChatThreadsCall,
    data: fetchChatThreadsResponse,
    isFetching: fetchChatThreadsLoading,
    error: fetchChatThreadsError,
}: any = useDefaultFetch(`/chat/user/${user.value?.uid}/threads`).json();

watch(fetchChatThreadsError, (error) => {
    console.log("Error fetching chat threads: ", error.value);
    const captureError = new ChatError(
        `Error fetching chat threads for user ${customer.value?.formattedPhoneNumber}: ${error.value?.message}`,
        {
            userId: customer.value?.id,
            errorMessage: error.value?.message,
            phoneNumber: customer.value?.formattedPhoneNumber,
        },
        "Error fetching chat threads",
    );
    Sentry.captureException(captureError);
});

const recentChats = computed(() => {
    return fetchChatThreadsResponse.value || [];
});
const formatPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return "";
    if (phoneNumber.startsWith("1")) {
        phoneNumber = phoneNumber.slice(1);
    }
    return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
};

onMounted(async () => {
    await fetchChatThreadsCall();
});
</script>
